// FINAL UPDATED VERSION:
// - Desktop panel uses Redbus-like accordion sections with dropdown arrows
// - Desktop header button renamed to "Clear all"
// - Desktop bottom "Clear all / Apply" buttons removed
// - Mobile panel kept exactly as your current working version

import React, { useState, useMemo } from "react";
import { FaSlidersH, FaTimes, FaSyncAlt, FaChevronDown } from "react-icons/fa";
import { useSearchCore, PALETTE, TIME_SLOTS } from "../_core";

export default function FilterPanel({ isMobile, sortBy, setSortBy }) {
  const {
    filters,
    setFilters,
    resetFilters,
    activeFilterCount,
    sortedBuses,
    setIsFilterOpen,
  } = useSearchCore();

  const [activeSection, setActiveSection] = useState("sort");

  // 🆕 desktop accordion open/close state
  const [openSections, setOpenSections] = useState({
    sort: true,
    departure: true,
    type: true,
    boarding: true,
    dropping: true,
  });

  const handleTimeSlotFilter = (slot) =>
    setFilters((prev) => ({
      ...prev,
      timeSlots: { ...prev.timeSlots, [slot]: !prev.timeSlots[slot] },
    }));

  const headerText = isMobile ? "Sort and filter buses" : "Filters";
  const resetText =
    activeFilterCount > 0
      ? `Clear all (${activeFilterCount})`
      : "Clear all";

  const boardingOptions = useMemo(() => {
    const set = new Set();
    (sortedBuses || []).forEach((bus) => {
      const raw =
        Array.isArray(bus?.boardingPoints) && bus.boardingPoints.length
          ? bus.boardingPoints
          : Array.isArray(bus?.boardingPointList)
          ? bus.boardingPointList
          : [];
      raw.forEach((p) => {
        const label =
          typeof p === "string" ? p : p?.name || p?.point || p?.label || "";
        if (label) set.add(label);
      });
    });
    return Array.from(set);
  }, [sortedBuses]);

  const droppingOptions = useMemo(() => {
    const set = new Set();
    (sortedBuses || []).forEach((bus) => {
      const raw =
        Array.isArray(bus?.droppingPoints) && bus.droppingPoints.length
          ? bus.droppingPoints
          : Array.isArray(bus?.droppingPointList)
          ? bus.droppingPointList
          : [];
      raw.forEach((p) => {
        const label =
          typeof p === "string" ? p : p?.name || p?.point || p?.label || "";
        if (label) set.add(label);
      });
    });
    return Array.from(set);
  }, [sortedBuses]);

  const RadioRow = ({ label, checked, onClick, isDesktop }) => {
    if (isDesktop) {
      return (
        <button
          type="button"
          onClick={onClick}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 10px",
            borderRadius: 8,
            border: `1px solid ${checked ? "#FECACA" : "#F0F0F0"}`,
            background: checked ? "#FEF2F2" : "#FAFAFA",
            marginBottom: 6,
            cursor: "pointer",
            transition: "background 0.15s, border-color 0.15s",
          }}
        >
          <span style={{ fontSize: 12, fontWeight: checked ? 600 : 500, color: checked ? "#DC2626" : "#374151", letterSpacing: "-0.01em" }}>
            {label}
          </span>
          <span
            style={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              border: `2px solid ${checked ? "#DC2626" : "#D1D5DB"}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              background: "#fff",
            }}
          >
            {checked && (
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#DC2626", display: "block" }} />
            )}
          </span>
        </button>
      );
    }
    return (
      <button
        type="button"
        onClick={onClick}
        className={`w-full flex items-center justify-between px-3 py-3 rounded-xl border text-sm mb-2 ${
          checked ? "border-[#D84E55] bg-[#FFF1F2]" : "border-gray-200 bg-white"
        }`}
      >
        <span className="text-gray-800 text-[14px] text-left">{label}</span>
        <span
          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
            checked ? "border-[#D84E55]" : "border-gray-300"
          }`}
        >
          {checked && (
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: PALETTE.primaryRed }}
            />
          )}
        </span>
      </button>
    );
  };

  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  // ---------------- DESKTOP VERSION ----------------
  if (!isMobile) {
    const desktopSections = [
      { key: "sort", label: "Sort by" },
      { key: "departure", label: "Departure time" },
      { key: "type", label: "Bus type" },
      { key: "boarding", label: "Boarding points" },
      { key: "dropping", label: "Dropping points" },
    ];

    const desktopSelectedBoarding = filters.boardingPoint || "";
    const desktopSelectedDropping = filters.droppingPoint || "";

    const renderDesktopSectionContent = (sectionKey) => {
      if (!openSections[sectionKey]) return null;

      switch (sectionKey) {
        case "sort":
          return (
            <div style={{ marginTop: 8 }}>
              <RadioRow
                label="Departure: Earliest First"
                checked={sortBy === "time-asc"}
                onClick={() => setSortBy("time-asc")}
                isDesktop
              />
              <RadioRow
                label="Departure: Latest First"
                checked={sortBy === "time-desc"}
                onClick={() => setSortBy("time-desc")}
                isDesktop
              />
            </div>
          );

        case "departure":
          return (
            <div style={{ marginTop: 8 }}>
              {Object.keys(TIME_SLOTS).map((slot) => (
                <RadioRow
                  key={slot}
                  label={slot}
                  checked={!!filters.timeSlots[slot]}
                  onClick={() => handleTimeSlotFilter(slot)}
                  isDesktop
                />
              ))}
            </div>
          );

        case "type":
          return (
            <div style={{ marginTop: 8 }}>
              <RadioRow
                label="All types"
                checked={!filters.type}
                onClick={() =>
                  setFilters((prev) => ({
                    ...prev,
                    type: "",
                  }))
                }
                isDesktop
              />
              <RadioRow
                label="AC"
                checked={filters.type === "AC"}
                onClick={() =>
                  setFilters((prev) => ({
                    ...prev,
                    type: "AC",
                  }))
                }
                isDesktop
              />
              <RadioRow
                label="Non-AC"
                checked={filters.type === "Non-AC"}
                onClick={() =>
                  setFilters((prev) => ({
                    ...prev,
                    type: "Non-AC",
                  }))
                }
                isDesktop
              />
            </div>
          );

        case "boarding":
          if (boardingOptions.length === 0) {
            return (
              <p style={{ marginTop: 8, fontSize: 11, color: "#9CA3AF" }}>
                Boarding points will appear here when available for this route.
              </p>
            );
          }
          return (
            <div style={{ marginTop: 8 }}>
              <RadioRow
                label="Any boarding point"
                checked={!desktopSelectedBoarding}
                onClick={() =>
                  setFilters((prev) => ({
                    ...prev,
                    boardingPoint: "",
                  }))
                }
                isDesktop
              />
              {boardingOptions.map((label) => (
                <RadioRow
                  key={label}
                  label={label}
                  checked={desktopSelectedBoarding === label}
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      boardingPoint: label,
                    }))
                  }
                  isDesktop
                />
              ))}
            </div>
          );

        case "dropping":
          if (droppingOptions.length === 0) {
            return (
              <p style={{ marginTop: 8, fontSize: 11, color: "#9CA3AF" }}>
                Dropping points will appear here when available for this route.
              </p>
            );
          }
          return (
            <div style={{ marginTop: 8 }}>
              <RadioRow
                label="Any dropping point"
                checked={!desktopSelectedDropping}
                onClick={() =>
                  setFilters((prev) => ({
                    ...prev,
                    droppingPoint: "",
                  }))
                }
                isDesktop
              />
              {droppingOptions.map((label) => (
                <RadioRow
                  key={label}
                  label={label}
                  checked={desktopSelectedDropping === label}
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      droppingPoint: label,
                    }))
                  }
                  isDesktop
                />
              ))}
            </div>
          );

        default:
          return null;
      }
    };

    return (
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          border: "1px solid #F0F0F0",
          boxShadow: "0 2px 12px rgba(0,0,0,0.045)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 20px 14px",
            borderBottom: "1px solid #F3F3F3",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FaSlidersH style={{ fontSize: 13, color: "#6B7280" }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: "#111", letterSpacing: "-0.01em" }}>
              {headerText}
            </span>
          </div>

          <button
            onClick={resetFilters}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              fontSize: 11,
              fontWeight: activeFilterCount > 0 ? 700 : 500,
              color: activeFilterCount > 0 ? "#DC2626" : "#9CA3AF",
              background: activeFilterCount > 0 ? "#FEF2F2" : "transparent",
              border: activeFilterCount > 0 ? "1px solid #FECACA" : "1px solid transparent",
              borderRadius: 6,
              padding: "4px 8px",
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            <FaSyncAlt style={{ fontSize: 9 }} /> {resetText}
          </button>
        </div>

        {/* Body: accordion sections */}
        <div style={{ flex: 1, overflowY: "auto", padding: "4px 0" }}>
          {desktopSections.map((sec) => (
            <div
              key={sec.key}
              style={{
                borderBottom: "1px solid #F7F7F8",
                padding: "12px 20px",
              }}
            >
              <button
                type="button"
                onClick={() => toggleSection(sec.key)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                }}
              >
                <span style={{ fontSize: 11, fontWeight: 700, color: "#374151", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                  {sec.label}
                </span>
                <FaChevronDown
                  style={{
                    fontSize: 9,
                    color: "#9CA3AF",
                    transition: "transform 0.2s",
                    transform: openSections[sec.key] ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </button>

              {renderDesktopSectionContent(sec.key)}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ---------------- MOBILE VERSION ----------------
  const mobileSections = [
    { key: "sort", label: "Sort by" },
    { key: "departure", label: "Departure time" },
    { key: "type", label: "Bus type" },
    { key: "boarding", label: "Boarding points" },
    { key: "dropping", label: "Dropping points" },
  ];

  const selectedBoarding = filters.boardingPoint || "";
  const selectedDropping = filters.droppingPoint || "";

  return (
    <>
      {/* FULLSCREEN CONTENT (height controlled by outer sheet) */}
      <div className="flex flex-col h-full bg-white overflow-hidden">
        {/* Header */}
        <div
          className="flex itemscenter justify-between px-4 py-3 border-b"
          style={{ borderColor: PALETTE.borderLight }}
        >
          <h3
            className="text-base font-bold"
            style={{ color: PALETTE.textDark }}
          >
            {headerText}
          </h3>
          <button
            type="button"
            onClick={() => setIsFilterOpen(false)}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left menu */}
          <div className="w-36 bg-gray-50 border-r overflow-y-auto">
            {mobileSections.map((sec) => (
              <button
                key={sec.key}
                type="button"
                onClick={() => setActiveSection(sec.key)}
                className={`w-full text-left px-4 py-3 text-[13px] border-b ${
                  activeSection === sec.key
                    ? "bg-white font-semibold text-[#D84E55]"
                    : "text-gray-700"
                }`}
                style={{
                  borderColor: "#F3F4F6",
                }}
              >
                {sec.label}
              </button>
            ))}
          </div>

          {/* Right content */}
          <div className="flex-1 px-4 py-4 overflow-y-auto">
            {activeSection === "sort" && (
              <div className="space-y-2">
                <RadioRow
                  label="Departure: Earliest First"
                  checked={sortBy === "time-asc"}
                  onClick={() => setSortBy("time-asc")}
                />
                <RadioRow
                  label="Departure: Latest First"
                  checked={sortBy === "time-desc"}
                  onClick={() => setSortBy("time-desc")}
                />
              </div>
            )}

            {activeSection === "departure" && (
              <div className="space-y-2">
                {Object.keys(TIME_SLOTS).map((slot) => (
                  <RadioRow
                    key={slot}
                    label={slot}
                    checked={!!filters.timeSlots[slot]}
                    onClick={() => handleTimeSlotFilter(slot)}
                  />
                ))}
              </div>
            )}

            {activeSection === "type" && (
              <div className="space-y-2">
                <RadioRow
                  label="All types"
                  checked={!filters.type}
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      type: "",
                    }))
                  }
                />
                <RadioRow
                  label="AC"
                  checked={filters.type === "AC"}
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      type: "AC",
                    }))
                  }
                />
                <RadioRow
                  label="Non-AC"
                  checked={filters.type === "Non-AC"}
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      type: "Non-AC",
                    }))
                  }
                />
              </div>
            )}

            {activeSection === "boarding" && (
              <div className="space-y-2">
                {boardingOptions.length === 0 ? (
                  <p className="text-xs text-gray-500">
                    Boarding points will appear here when available for this
                    route.
                  </p>
                ) : (
                  <>
                    <RadioRow
                      label="Any boarding point"
                      checked={!selectedBoarding}
                      onClick={() =>
                        setFilters((prev) => ({
                          ...prev,
                          boardingPoint: "",
                        }))
                      }
                    />

                    {boardingOptions.map((label) => (
                      <RadioRow
                        key={label}
                        label={label}
                        checked={selectedBoarding === label}
                        onClick={() =>
                          setFilters((prev) => ({
                            ...prev,
                            boardingPoint: label,
                          }))
                        }
                      />
                    ))}
                  </>
                )}
              </div>
            )}

            {activeSection === "dropping" && (
              <div className="space-y-2">
                {droppingOptions.length === 0 ? (
                  <p className="text-xs text-gray-500">
                    Dropping points will appear here when available for this
                    route.
                  </p>
                ) : (
                  <>
                    <RadioRow
                      label="Any dropping point"
                      checked={!selectedDropping}
                      onClick={() =>
                        setFilters((prev) => ({
                          ...prev,
                          droppingPoint: "",
                        }))
                      }
                    />

                    {droppingOptions.map((label) => (
                      <RadioRow
                        key={label}
                        label={label}
                        checked={selectedDropping === label}
                        onClick={() =>
                          setFilters((prev) => ({
                            ...prev,
                            droppingPoint: label,
                          }))
                        }
                      />
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Bottom Buttons */}
        <div
          className="px-4 py-3 border-t bg-white flex gap-3 pb-4"
          style={{
            borderColor: PALETTE.borderLight,
          }}
        >
          <button
            type="button"
            onClick={() => resetFilters()}
            className="flex-1 h-11 rounded-full border text-[14px] font-semibold"
            style={{
              borderColor: "#D1D5DB",
              color: PALETTE.textDark,
              backgroundColor: "white",
            }}
          >
            Clear all
          </button>

          <button
            type="button"
            onClick={() => setIsFilterOpen(false)}
            className="flex-1 h-11 rounded-full text-[14px] font-semibold text-white"
            style={{ backgroundColor: PALETTE.primaryRed }}
          >
            Apply
          </button>
        </div>
      </div>
    </>
  );
}

